import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { IUserLookupService } from "../../lookup/model/IUserLookupService";
import { ISystemConfigService } from "../../profile/ISystemConfigService";
import { TranslateService } from "@ngx-translate/core";

import { PricingService } from "../../lookup/pricing/PricingService";
import { ITraderGroupLookupService } from "../../lookup/model/ITraderGroupLookupService";
import { SysFeatures } from "../core/SysFeatures";
import { StrategyTooltip } from "../../adapter/orderBlotter/StrategyTooltip";
import { BfxSysConfigUtilService } from "shared/auth-services";
import { OrderDataLockLogger } from "shared/opt-in-logger";
import { OrderDataModelUpdateSource } from "./OrderDataModelUpdateSource";
import { OrdersTableMode } from "shared/cubiq-services/model/OrdersTableMode";

/**
 * Utility service used to return the correct labels for trailing pips/trigger for any specified order.
 * Labels are used both in blotter columns and in the TSL tooltips referened from the StatusIconshelper/
 *
 * Stores references to both Base and dealt pricing services as well as the current display state of the blotter.
 *
 * While the invert mode function is constant once the blotter is created, both it and the
 * table mode currently need to be updated before each retrieval - ideally, these should only ever be updated
 * when the blotter itself changes mode
 *
 * Needs to cater for Mkt & Invert TSLs, displayed in Base Mode, Non-base mode, Dealt Mode
 */
export class TSLLabelHelper {
  private _ordersTableMode: OrdersTableMode;
  private _invertedBaseMode: (order: any) => boolean;

  constructor(
    protected basePricingService: PricingService,
    protected dealtPricingService: PricingService,
  ) {}

  /**
   * The Blotter display mode - either in base or dealt
   */
  public get ordersTableMode(): OrdersTableMode {
    return this._ordersTableMode;
  }
  public set ordersTableMode(value: OrdersTableMode) {
    this._ordersTableMode = value;
  }

  /**
   * FMethod that determines if the specified order is inverted, based on the current invert mode
   * of the ladder - if not defined, then display covention is assumed to be in Base.
   */
  public get invertedBaseMode(): (order: any) => boolean {
    return this._invertedBaseMode;
  }
  public set invertedBaseMode(value: (order: any) => boolean) {
    this._invertedBaseMode = value;
  }

  /**
   * The TSL trigger rate for inactive TSLs
   * @param order
   * @returns
   */
  public getTrailingTriggerLabel(order: CubiqTS.OrderDataImpl): string {
    switch (this._ordersTableMode) {
      case OrdersTableMode.DEALT:
      case OrdersTableMode.NDF_DEALT:
        return this.dealtPricingService.getBlotterTrailingTrigger(order);
        break;
      case OrdersTableMode.NDF_BASE:
      case OrdersTableMode.BASE:
        let pricingService: PricingService = this.basePricingService;
        if (this._invertedBaseMode && this._invertedBaseMode(order)) {
          // This is ladder with pairs displayed as inverted - these methods below are identical for both
          // base and dealt pricing services
          return pricingService.getInvertTrailingTrigger(order);
        }
        return pricingService.getBaseTrailingTrigger(order);
      default:
        break;
    }
  }

  /**
   * The Current TSL trailing pips value
   * @param order
   * @returns
   */
  public getTrailingPipsLabel(order: CubiqTS.OrderDataImpl): string {
    switch (this._ordersTableMode) {
      case OrdersTableMode.DEALT:
      case OrdersTableMode.NDF_DEALT:
        return this.dealtPricingService.getBlotterTrailingPips(order);
        break;
      case OrdersTableMode.NDF_BASE:
      case OrdersTableMode.BASE:
        let pricingService: PricingService = this.basePricingService;
        if (this._invertedBaseMode && this._invertedBaseMode(order)) {
          // This is ladder with pairs displayed as inverted - these methods below are identical for both
          // base and dealt pricing services
          return pricingService.getInvertTrailingPips(order);
        }
        return pricingService.getBaseTrailingPips(order);
    }
  }
}
